import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { DarkModeToggle } from "../components/DarkModeToggle";

export default function PrivacyPolicy() {
  const nav = useNavigate();

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120]">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button data-testid="privacy-back-btn" onClick={() => nav(-1)} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800">
            <ChevronLeft className="w-5 h-5 text-slate-500" />
          </button>
          <h1 className="text-lg font-extrabold text-slate-900 dark:text-white flex-1">Privacy Policy</h1>
          <DarkModeToggle />
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 sm:p-8 space-y-8">

          <div>
            <p className="text-xs text-slate-400 mb-1">Version 1.0 — Effective April 2026</p>
            <h2 className="text-2xl font-extrabold text-slate-900 dark:text-white">Privacy Policy</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 leading-relaxed">
              This Privacy Policy explains how National Driving Guild ("the Platform", "we", "us") collects, uses, stores, and protects your personal data when you use our services.
            </p>
          </div>

          <Section title="1. What Data We Collect">
            <p>When you use the Platform, we may collect the following information:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li><strong>Account data:</strong> Name, email address, password (encrypted), role (learner/instructor).</li>
              <li><strong>Profile data:</strong> Postcode, phone number, learning preferences, progress data.</li>
              <li><strong>Booking data:</strong> Lesson dates, times, durations, instructor details, cancellation history.</li>
              <li><strong>Payment data:</strong> Transaction records, amounts, payment status. Card details are processed securely by Stripe and are never stored on our servers.</li>
              <li><strong>Usage data:</strong> Check-in responses, reflection entries (including any audio you save), skill progress, lesson preparation status.</li>
              <li><strong>Technical data:</strong> Browser type, device information, IP address, access timestamps.</li>
            </ul>
          </Section>

          <Section title="2. Why We Collect Your Data">
            <p>We use your data to:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Create and manage your account.</li>
              <li>Process lesson bookings and payments.</li>
              <li>Provide personalised learning content and progress tracking.</li>
              <li>Send booking confirmations, reminders, and notifications.</li>
              <li>Enable instructor-learner communication within the Platform.</li>
              <li>Improve the Platform through anonymised usage analysis.</li>
              <li>Comply with legal and regulatory obligations.</li>
            </ul>
          </Section>

          <Section title="3. Booking & Payment Data">
            <p>When you book a lesson, we store the booking details (date, time, duration, instructor, price) and payment status. All card payments are processed by <strong>Stripe</strong>, a PCI-compliant payment processor. We do not store your full card number, CVV, or bank details.</p>
            <p className="mt-2">Payment transaction records (amounts, dates, status) are retained for accounting and dispute resolution purposes.</p>
          </Section>

          <Section title="4. Contact & Reminder Usage">
            <p>We use your email address to send:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Booking confirmations and changes.</li>
              <li>Lesson reminders (typically 24 hours and 2 hours before your lesson).</li>
              <li>Account-related notifications (password resets, policy updates).</li>
            </ul>
            <p className="mt-2">We will not sell your email address to third parties or send unsolicited marketing without your consent.</p>
          </Section>

          <Section title="5. Account & Profile Data">
            <p>Your account profile data (name, email, learning preferences, progress) is stored securely and is accessible only to you and, where relevant, your assigned instructor. Instructors can see your lesson history, skill progress, and check-in data to provide better instruction.</p>
          </Section>

          <Section title="6. Data Retention & Deletion">
            <p>We retain your personal data for as long as your account is active. If you request account deletion:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Your personal profile data will be deleted or anonymised.</li>
              <li>Payment transaction records may be retained for up to 7 years for legal and accounting purposes.</li>
              <li>Anonymised usage data may be retained for Platform improvement.</li>
            </ul>
            <p className="mt-2">To request deletion of your data, please contact us through the Platform.</p>
          </Section>

          <Section title="7. Third-Party Providers">
            <p>We use the following third-party services to operate the Platform:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li><strong>Stripe</strong> — Payment processing. Subject to <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Stripe's Privacy Policy</a>.</li>
              <li><strong>MongoDB</strong> — Database hosting and storage.</li>
              <li><strong>Emergent</strong> — Platform hosting and deployment.</li>
            </ul>
            <p className="mt-2">These providers process data on our behalf and are bound by their own privacy and security commitments.</p>
          </Section>

          <Section title="8. Your Rights">
            <p>You have the right to:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li><strong>Access</strong> the personal data we hold about you.</li>
              <li><strong>Correct</strong> any inaccurate or incomplete data.</li>
              <li><strong>Delete</strong> your account and associated personal data.</li>
              <li><strong>Object</strong> to processing of your data for specific purposes.</li>
              <li><strong>Withdraw consent</strong> where processing is based on consent.</li>
            </ul>
            <p className="mt-2">To exercise any of these rights, please contact us through the Platform.</p>
          </Section>

          <Section title="9. Data Security">
            <p>We take reasonable measures to protect your personal data, including:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Encrypted passwords (bcrypt hashing).</li>
              <li>Secure HTTPS connections.</li>
              <li>HTTP-only authentication cookies.</li>
              <li>Access controls limiting who can view your data.</li>
            </ul>
            <p className="mt-2">While we strive to protect your data, no method of transmission over the Internet is 100% secure.</p>
          </Section>

          <Section title="10. Reflection Evidence & Canary Participation">
            <p>Some instructor and learner reflection audio may be moved through an internal <strong>Media Boundary</strong> pathway so we can retain a durable, verifiable record of what was captured. Where this happens:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li><strong>Internal transfer:</strong> A copy of the reflection audio you save is transferred from the point of capture into our evidence pathway using an authenticated, time-limited internal token. The audio is not shared with third parties.</li>
              <li><strong>Integrity verification:</strong> We keep a cryptographic checksum (SHA-256) of the transferred audio so we can verify the file has not been altered. This checksum, and a short-lived internal token that was used to authorise the transfer, may be retained alongside the evidence record.</li>
              <li><strong>Immutable evidence record:</strong> Once accepted, the evidence record itself is not edited or overwritten. If something needs to be corrected, we create a new record rather than changing the original. This keeps a clear history for governance, safety, provenance, and audit purposes.</li>
              <li><strong>Canary participation and frozen admission record:</strong> During limited canary phases, participation may create a small, immutable <em>admission record</em> that is attached to the evidence row. It records who was authorised to participate, when, and under which canary window — nothing more.</li>
              <li><strong>Downstream consumers may be unauthorised:</strong> Evidence created during these phases may carry the label <code>routing_disposition = "no_consumers_authorised"</code>. This means it is <strong>not</strong> yet used to score, analyse, adapt, or deliver anything to any learner-facing educational feature. It is retained only so we can verify the pathway is working safely.</li>
              <li><strong>Retention and deletion:</strong> These evidence and admission records follow the same account-lifecycle retention described in Section 6. If you request account deletion, personal data linked to these records is deleted or anonymised in line with our account-deletion policy. Governance records that are needed to prove the pathway operated safely may be retained in de-identified form.</li>
            </ul>
            <p className="mt-2">You control what is captured. If you do not save a reflection, no evidence record is created for it. If you choose to save one, the disclosures above apply.</p>
          </Section>

          <Section title="11. Changes to This Policy">
            <p>We may update this Privacy Policy from time to time. Changes will be communicated through the Platform. Continued use after changes constitutes acceptance.</p>
          </Section>

          <Section title="12. Contact">
            <p>If you have questions about this Privacy Policy or your personal data, please contact us through the Platform or at the details provided in your account settings.</p>
          </Section>

          <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
            <p className="text-xs text-slate-400 leading-relaxed">
              This policy was last updated in April 2026.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-base font-extrabold text-slate-900 dark:text-white mb-2">{title}</h3>
      <div className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
        {children}
      </div>
    </div>
  );
}
